import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { DialogProps } from '@ast/magma/components/dialog';
import { Toggle } from '@ast/magma/components/toggle';

import { ValidationFaultType } from '@app/queryTyping';

import { FieldInfoGroupField } from '@app/common/configurable-wizards';
import { SelectableGroupFieldFragment } from '@app/common/configurable-wizards/queries/queryTyping/all-fields';
import { DEFAULT_COUNTRY } from '@app/common/types/locales';

import { AddressSuggestionModal, Addresses } from '@app/widgets/contacts-and-settings/components/AddressSuggestionModal/AddressSuggestionModal';
import { SubmitModal } from '@app/widgets/contacts-and-settings/components/SubmitModal';
import { useAddressFormContext } from '@app/widgets/contacts-and-settings/contexts/AddressFormContext';
import { OnErrorProps } from '@app/widgets/contacts-and-settings/hooks/useSubmitContact';
import { useUserSettingsTranslation } from '@app/widgets/contacts-and-settings/hooks/useUserSettingsTranslation';
import {
  AddressFormCache,
  AddressItemFields,
  AddressTypes,
  InternationalAddressFields,
  USAddressFields,
} from '@app/widgets/contacts-and-settings/types';
import { getProvidedAddress, getSuggestedAddress, isPhysicalAddressField } from '@app/widgets/contacts-and-settings/utils/address';
import { getEmptyFields, setFieldValues } from '@app/widgets/contacts-and-settings/utils/fieldValues';

import styles from './AddressEditForm.pcss';

export interface AddressEditFormProps {
  readonly field: SelectableGroupFieldFragment;
  readonly dialogOptions: DialogProps;
  readonly successMessage?: string;
  readonly valuesModifier?: Record<string, string | null>;
  readonly onClose?: () => void;
}

export const AddressEditForm: React.FC<AddressEditFormProps> = ({
  field: parentField,
  successMessage,
  dialogOptions,
  valuesModifier = {},
  onClose,
}) => {
  const { t } = useUserSettingsTranslation();
  const {
    country,
    isForeignAddressEnabled,
    isApoFpoFeatureEnabled,
    isApoFpoEnabled,
  } = useAddressFormContext();
  const isPhysicalAddress = isPhysicalAddressField(parentField);
  const [isMilitary, setIsMilitary] = useState(
    parentField.selectedFieldGroupId === AddressItemFields.MilitaryMailingAddressGroup,
  );
  const [isToggled, setIsToggled] = useState(false); // flag to clear form values when user toggle apo/fpo
  const [isSuggestion, setIsSuggestion] = useState(false); // flag to switch modal content sumbit/suggestion
  const [formCache, setFormCache] = useState<AddressFormCache | null>(null);
  const [addresses, setAddresses] = useState<Addresses | null>(null);

  useEffect(() => {
    const isForeignCountry = country && country !== DEFAULT_COUNTRY;

    // disable military addresses if changed to international
    if (isForeignCountry && !isApoFpoEnabled && isMilitary) {
      setIsMilitary(false);
    }
  }, [isApoFpoEnabled]);

  const field = useMemo(() => {
    const currentFieldGroupId = isMilitary
      ? AddressItemFields.MilitaryMailingAddressGroup
      : AddressItemFields.AddressGroup;

    const selectedField = parentField.fieldGroups.find(
      (item) => item?.id === currentFieldGroupId,
    ) as FieldInfoGroupField;

    const mailingType = isMilitary
      ? AddressTypes.MilitaryMailing
      : AddressTypes.Mailing;

    const cachedField = formCache
      ? formCache[AddressItemFields.AddressGroup]
      : {};

    return setFieldValues(selectedField, {
      ...valuesModifier,
      ...(isForeignAddressEnabled && country ? {
        Country: country,
        ...getEmptyFields(
          country === DEFAULT_COUNTRY
            ? InternationalAddressFields
            : USAddressFields,
        ),
      } : {}),
      ...(isToggled ? {
        City: null,
        State: null,
        MilitaryMailingCities: null,
      } : {}),
      Type: isPhysicalAddress
        ? AddressTypes.Physical
        : mailingType,
      ...cachedField,
    });
  }, [parentField, formCache, isToggled, isMilitary, country]) as FieldInfoGroupField;

  const switchMilitary = useCallback(() => {
    if (!isToggled) {
      setIsToggled(true);
    }

    setIsMilitary(!isMilitary);
  }, [isMilitary]);

  const handleErrors = ({ errors, values }: OnErrorProps) => {
    const isUSPSFault = errors.find((error) => (
      error.faultType === ValidationFaultType.OTHER
      && error.field
      && error.context
    ));

    if (isUSPSFault) {
      setAddresses({
        providedAddress: getProvidedAddress(values),
        suggestedAddress: getSuggestedAddress(errors),
      });

      setFormCache(values);
      setIsSuggestion(true);
    }
  };

  const handleClose = () => {
    setIsSuggestion(false);
  };

  return isSuggestion ? (
    <AddressSuggestionModal
      {...dialogOptions}
      {...addresses}
      formCache={formCache}
      successMessage={successMessage}
      onClose={handleClose}
      onSuccess={onClose}
    />
  ) : (
    <SubmitModal
      open
      field={field}
      dialogOptions={dialogOptions}
      successMessage={successMessage}
      onClose={onClose}
      onError={handleErrors}
    >
      {(isApoFpoEnabled && isApoFpoFeatureEnabled && !isPhysicalAddress) && (
        <div className={styles.toggle}>
          <span>
            {t(
              'address-edit-form.apo-fpo-toggle.label|APO/FPO toggle',
              'APO/FPO address',
            )}
          </span>
          <Toggle
            data-stable-name="APOFPOToggle"
            checked={isMilitary}
            onChange={switchMilitary}
          />
        </div>
      )}
    </SubmitModal>
  );
};
